import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";

import { Dialog, DialogContent, Paper, Typography, Box, makeStyles } from "@material-ui/core";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  pairingCodeDisplay: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    backgroundColor: "#e8f5e9",
    borderRadius: 16,
    minWidth: 256,
    minHeight: 256,
  },
  pairingCodeText: {
    fontSize: 42,
    fontWeight: "bold",
    fontFamily: "monospace",
    letterSpacing: 8,
    color: "#1b5e20",
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 3),
    backgroundColor: "#fff",
    borderRadius: 8,
    border: "2px dashed #4caf50",
  },
  pairingCodeIcon: {
    fontSize: 48,
    color: "#4caf50",
  },
  pairingInstructions: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    maxWidth: 300,
  },
}));

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [usePairingCode, setUsePairingCode] = useState(false);
  const [status, setStatus] = useState("");

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
        setPairingCode(data.pairingCode || "");
        setUsePairingCode(data.usePairingCode || false);
        setStatus(data.status);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-whatsappSession`, (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
        setPairingCode(data.session.pairingCode || "");
        setUsePairingCode(data.session.usePairingCode || false);
        setStatus(data.session.status);
      }

      // Fecha se conectou (qrcode e pairingCode vazios e status CONNECTED)
      if (data.action === "update" && 
          data.session.id === whatsAppId && 
          data.session.qrcode === "" && 
          !data.session.pairingCode &&
          data.session.status === "CONNECTED") {
        onClose();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [whatsAppId, onClose, socketManager]);

  // Formata o pairing code com hífen no meio (ex: 1234-5678)
  const formatPairingCode = (code) => {
    if (!code || code.length !== 8) return code;
    return `${code.slice(0, 4)}-${code.slice(4)}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" scroll="paper">
      <DialogContent>
        <Paper elevation={0} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: "20px" }}>
            {usePairingCode && pairingCode ? (
              // Instruções para Pairing Code
              <>
                <Typography variant="h2" component="h2" color="textPrimary" gutterBottom style={{ fontFamily: "Montserrat", fontWeight: "bold", fontSize: "20px" }}>
                  Código de Pareamento
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  1. Abra o WhatsApp no seu celular
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  2. Vá em <strong>Configurações</strong> → <strong>Dispositivos conectados</strong>
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  3. Toque em <strong>Conectar um dispositivo</strong>
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  4. Toque em <strong>Conectar com número de telefone</strong>
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  5. Digite o código ao lado e confirme
                </Typography>
              </>
            ) : (
              // Instruções para QR Code
              <>
                <Typography variant="h2" component="h2" color="textPrimary" gutterBottom style={{ fontFamily: "Montserrat", fontWeight: "bold", fontSize: "20px" }}>
                  {i18n.t("qrCodeModal.title")}
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  {i18n.t("qrCodeModal.steps.one")}
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  {i18n.t("qrCodeModal.steps.two.partOne")} <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"></path></svg> {i18n.t("qrCodeModal.steps.two.partTwo")} <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"></path></svg> {i18n.t("qrCodeModal.steps.two.partThree")}
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  {i18n.t("qrCodeModal.steps.three")}
                </Typography>
                <Typography variant="body1" color="textPrimary" gutterBottom>
                  {i18n.t("qrCodeModal.steps.four")}
                </Typography>
              </>
            )}
          </div>
          <div>
            {usePairingCode && pairingCode ? (
              // Exibe Pairing Code
              <Box className={classes.pairingCodeDisplay}>
                <PhoneAndroidIcon className={classes.pairingCodeIcon} />
                <Typography variant="body2" color="textSecondary">
                  Digite este código no WhatsApp:
                </Typography>
                <Typography className={classes.pairingCodeText}>
                  {formatPairingCode(pairingCode)}
                </Typography>
                <Typography variant="caption" color="textSecondary" className={classes.pairingInstructions}>
                  O código expira em alguns minutos. Se expirar, feche e abra novamente.
                </Typography>
              </Box>
            ) : qrCode ? (
              // Exibe QR Code
              <QRCode value={qrCode} size={256} />
            ) : (
              // Aguardando
              <Box className={classes.pairingCodeDisplay} style={{ backgroundColor: "#f5f5f5" }}>
                <Typography variant="body1" color="textSecondary">
                  {status === "pairingcode" ? "Gerando código..." : i18n.t("qrCodeModal.waiting")}
                </Typography>
              </Box>
            )}
          </div>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
